import xhrService from '../xhrService';

export type BillingAddonRecurrence = 'one_time' | 'recurring';

export interface BillingAddon {
  id: number;
  tenant_id: number;
  name: string;
  code: string;
  status: 'active' | 'inactive';
  cost_value?: number | null;
  recurrence_type?: BillingAddonRecurrence;
  divisions?: { id: number; name: string }[];
}

export interface BillingAddonFormAssignment {
  id: number;
  billing_addon_id: number;
  project_inspection_template_id: number;
  created_at: string;
  removed_at: string | null;
  billingAddon?: BillingAddon;
}

export interface AddonImpactRow {
  divisionId: number;
  divisionName: string;
  projectCount: number;
}

const billingAddonApi = {
  // ─── Admin ──────────────────────────────────────────────────────────────────

  index: (status?: 'active' | 'inactive'): Promise<BillingAddon[]> =>
    xhrService
      .get('/api/billing-addons', { params: status ? { status } : {} })
      .then(res => res.data),

  get: (id: number): Promise<BillingAddon> =>
    xhrService.get(`/api/billing-addons/${id}`).then(res => res.data),

  checkName: (name: string, excludeId?: number): Promise<{ unique: boolean }> =>
    xhrService
      .get('/api/billing-addons/check-name', { params: { name, ...(excludeId ? { excludeId } : {}) } })
      .then(res => res.data),

  create: (data: { name: string; code: string; status: 'active' | 'inactive'; divisionIds?: number[]; cost_value?: number | null; recurrence_type?: BillingAddonRecurrence }): Promise<BillingAddon> =>
    xhrService.post('/api/billing-addons', data).then(res => res.data),

  update: (id: number, data: { name?: string; code?: string; status?: 'active' | 'inactive'; divisionIds?: number[]; cost_value?: number | null; recurrence_type?: BillingAddonRecurrence }): Promise<BillingAddon> =>
    xhrService.put(`/api/billing-addons/${id}`, data).then(res => res.data),

  destroy: (id: number): Promise<void> =>
    xhrService.delete(`/api/billing-addons/${id}`).then(() => undefined),

  setStatus: (id: number, status: 'active' | 'inactive'): Promise<BillingAddon> =>
    xhrService.post(`/api/billing-addons/${id}/status`, { status }).then(res => res.data),

  impact: (id: number): Promise<AddonImpactRow[]> =>
    xhrService.get(`/api/billing-addons/${id}/impact`).then(res => res.data),

  // ─── Division ───────────────────────────────────────────────────────────────

  division: {
    index: (divisionId: number): Promise<BillingAddon[]> =>
      xhrService.get(`/api/divisions/${divisionId}/billing-addons`).then(res => res.data),

    available: (divisionId: number): Promise<BillingAddon[]> =>
      xhrService.get(`/api/divisions/${divisionId}/billing-addons/available`).then(res => res.data),

    assign: (divisionId: number, addonId: number): Promise<BillingAddon[]> =>
      xhrService.post(`/api/divisions/${divisionId}/billing-addons`, { addonId }).then(res => res.data),

    removeImpact: (divisionId: number, addonId: number): Promise<{ formCount: number }> =>
      xhrService.get(`/api/divisions/${divisionId}/billing-addons/${addonId}/remove-impact`).then(res => res.data),

    remove: (divisionId: number, addonId: number): Promise<void> =>
      xhrService.delete(`/api/divisions/${divisionId}/billing-addons/${addonId}`).then(() => undefined),
  },

  // ─── Form ───────────────────────────────────────────────────────────────────

  form: {
    index: (formId: number): Promise<BillingAddonFormAssignment[]> =>
      xhrService.get(`/api/project-forms/${formId}/billing-addons`).then(res => res.data),

    available: (formId: number): Promise<BillingAddon[]> =>
      xhrService.get(`/api/project-forms/${formId}/billing-addons/available`).then(res => res.data),

    hasDivisionAddons: (formId: number): Promise<{ hasDivisionAddons: boolean }> =>
      xhrService.get(`/api/project-forms/${formId}/billing-addons/has-division-addons`).then(res => res.data),

    assign: (formId: number, addonId: number): Promise<BillingAddonFormAssignment[]> =>
      xhrService.post(`/api/project-forms/${formId}/billing-addons`, { addonId }).then(res => res.data),

    remove: (formId: number, assignmentId: number): Promise<void> =>
      xhrService.delete(`/api/project-forms/${formId}/billing-addons/${assignmentId}`).then(() => undefined),
  },
};

export default billingAddonApi;
